import React, { useCallback, useMemo } from "react";
import { SummaryV3CageListTooltip, SummaryV3Tooltip } from "./SummaryV3Tooltip";
import { translateText } from "../../../../shared/utils/historyTranslation";
import { getCenterTableViewModel } from "../utils/summaryDashboardV3Utils";

export default React.memo(function SummaryV3CenterTable({
  summaries,
  visibleWindowLabel,
  canNavigateCenters,
  enteringCenterKeys,
  windowAnimating,
  windowShiftDirection,
  hideNormalCenters,
  onPreviousCenters,
  onNextCenters,
  onToggleNormalCenters,
  onOpenCenter,
  translate,
}) {
  const t = useCallback((key, fallback) => translateText(translate, key, fallback), [translate]);
  return (
    <section className="summary-v3__tablePanel">
      <div className="summary-v3__panelHead">
        <h3>{t("summary_v3_center_summary_title", "Resumen por centro")}</h3>
        <div className="summary-v3__panelActions">
          <div className="summary-v3__windowControls">
            <button
              type="button"
              className="summary-v3__windowButton"
              onClick={onPreviousCenters}
              disabled={!canNavigateCenters}
              title={t("summary_v3_show_previous_centers", "Mostrar centros anteriores")}
            >
              {"<"}
            </button>
            <button
              type="button"
              className="summary-v3__windowButton"
              onClick={onNextCenters}
              disabled={!canNavigateCenters}
              title={t("summary_v3_show_next_centers", "Mostrar centros siguientes")}
            >
              {">"}
            </button>
          </div>
          <button
            type="button"
            className={`summary-v3__mapToggle ${hideNormalCenters ? "is-active" : ""}`}
            onClick={onToggleNormalCenters}
          >
            {hideNormalCenters
              ? t("summary_v3_show_normal", "Mostrar normales")
              : t("summary_v3_hide_normal", "Ocultar normales")}
          </button>
          <span>{visibleWindowLabel}</span>
        </div>
      </div>
      <div className="summary-v3__tableWrap">
        <table className="summary-v3__table">
          <thead>
            <tr>
              <th>{t("centro", "Centro")}</th>
              <th>{t("summary_v3_avg_o2", "O2 promedio")}</th>
              <th>{t("summary_v3_cages", "Jaulas")}</th>
              <th>{t("summary_v3_below_threshold", "Bajo Umbral")}</th>
              <th>{t("summary_v3_legend_injecting", "Inyectando")}</th>
              <th>{t("summary_v3_no_injection", "Sin inyeccion")}</th>
              <th>{t("summary_v3_legend_problems", "Problemas")}</th>
              <th>{t("summary_v3_generators", "Generadores")}</th>
              <th>{t("summary_v3_worst_case", "Peor caso")}</th>
              <th>{t("ultima_medicion", "Ultima medicion")}</th>
              <th>{t("status", "Estado")}</th>
            </tr>
          </thead>
          <tbody>
            {summaries.map((summary) => (
              <SummaryV3CenterTableRow
                key={summary.centerKey}
                summary={summary}
                entering={enteringCenterKeys?.has(summary.centerKey)}
                shifting={windowAnimating && !enteringCenterKeys?.has(summary.centerKey)}
                shiftDirection={windowShiftDirection}
                onOpenCenter={onOpenCenter}
                translate={translate}
              />
            ))}
          </tbody>
        </table>
        {!summaries.length ? (
          <div className="summary-v3__empty">{t("summary_v3_no_centers", "No hay centros para mostrar.")}</div>
        ) : null}
      </div>
    </section>
  );
});

const SummaryV3CenterTableRow = React.memo(function SummaryV3CenterTableRow({
  summary,
  entering,
  shifting,
  shiftDirection,
  onOpenCenter,
  translate,
}) {
  const viewModel = useMemo(() => getCenterTableViewModel(summary, translate), [summary, translate]);
  const handleOpenCenter = useCallback(() => onOpenCenter(summary), [onOpenCenter, summary]);
  const t = useCallback((key, fallback) => translateText(translate, key, fallback), [translate]);

  return (
    <tr
      className={`summary-v3__tableRow summary-v3__tableRow--${summary.status} ${entering ? "is-entering" : ""} ${
        shifting ? `is-shifting-${shiftDirection}` : ""
      }`}
    >
      <td>
        <button type="button" onClick={handleOpenCenter}>
          <span className={`summary-v3__statusDot summary-v3__statusDot--${summary.status}`} />
          {summary.centerName}
        </button>
      </td>
      <td className={`summary-v3__${viewModel.o2Tone}Text`}>
        <SummaryV3Tooltip
          value={viewModel.o2Value}
          title={t("summary_v3_avg_oxygen", "Oxigeno promedio")}
          size="o2"
          rows={viewModel.o2Rows}
          translate={translate}
        />
      </td>
      <td>
        <SummaryV3Tooltip
          value={summary.cagesTotal}
          title={t("summary_v3_monitored_cages", "Jaulas monitoreadas")}
          size="cages"
          rows={viewModel.cageRows}
          translate={translate}
        />
      </td>
      <td>
        <SummaryV3CageListTooltip
          value={summary.counts.belowThreshold}
          title={t("summary_v3_cages_below_threshold", "Jaulas bajo Umbral")}
          cages={viewModel.belowThresholdCages}
          size="below"
          translate={translate}
        />
      </td>
      <td>
        <SummaryV3CageListTooltip
          value={summary.counts.injecting}
          title={t("summary_v3_cages_injecting", "Jaulas inyectando")}
          cages={viewModel.injectingCages}
          size="injecting"
          translate={translate}
        />
      </td>
      <td>
        <SummaryV3CageListTooltip
          value={summary.counts.belowWithoutInjection}
          title={t("summary_v3_below_threshold_without_injection", "Bajo Umbral sin inyeccion")}
          cages={viewModel.belowWithoutInjectionCages}
          size="noInjection"
          translate={translate}
        />
      </td>
      <td>
        <SummaryV3Tooltip
          value={viewModel.problemCount}
          title={t("summary_v3_legend_problems", "Problemas")}
          rows={viewModel.problemRows}
          extraRows={viewModel.problemExtraRows}
          size="problems"
          translate={translate}
        />
      </td>
      <td>
        <SummaryV3Tooltip
          value={viewModel.generatorsLabel}
          title={t("summary_v3_generators", "Generadores")}
          size="generators"
          rows={viewModel.generatorRows}
          translate={translate}
        />
      </td>
      <td>
        <SummaryV3Tooltip
          value={viewModel.worstCageLabel}
          title={t("summary_v3_worst_center_case", "Peor caso del centro")}
          rows={viewModel.worstCageRows}
          size="worst"
          translate={translate}
        /> 
      </td>
      <td>
        <SummaryV3Tooltip
          value={viewModel.lastUpdateLabel}
          title={t("ultima_medicion", "Ultima medicion")}
          rows={viewModel.lastUpdateRows}
          size="lastUpdate"
          translate={translate}
        />
      </td>
      <td>
        <SummaryV3Tooltip
          value={( 
            <span className={`summary-v3__badge summary-v3__badge--${summary.status}`}>
              {viewModel.statusLabel}
            </span>
          )}
          title={t("summary_v3_center_status", "Estado del centro")}
          size="status"
          rows={viewModel.statusRows}
          translate={translate}
        />
      </td>
    </tr>
  );
});
